import type { UserSessionRow } from '@pqrs/contracts';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@xiltrion/ui/item';
import { Spinner } from '@xiltrion/ui/spinner';
import { avisar } from '@xiltrion/ui/sonner';
import { Card, EmptyState, ErrorState, LoadingState, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { useSession } from '@/app/session';
import { useCloseOtherSessions, useCloseSession, useMySessions } from './api';

/**
 * Seguridad: la cuenta (contraseña, verificación en dos pasos, ingreso institucional) es de la suite Datalog
 * (GUIA-AGENTES §3.7); aquí solo las sesiones abiertas en SGDEA.
 */
export function SecurityTab() {
  return (
    <div className="flex flex-col gap-4">
      <AccountCard />
      <SessionsCard />
    </div>
  );
}

function AccountCard() {
  const { me } = useSession();
  return (
    <Card title="Tu cuenta">
      <p className="text-sm m-0 text-muted-foreground">Entras a SGDEA con tu cuenta de la suite Datalog. La contraseña, la verificación en dos pasos y la app autenticadora con la que también firmas se administran en tu perfil de la suite.</p>
      {me?.suite.profileUrl && <Button asChild variant="outline" size="sm" className="mt-3"><a href={me.suite.profileUrl} target="_blank" rel="noreferrer"><Icono nombre="mdi:open-in-new" /> Ir a mi perfil en la suite</a></Button>}
    </Card>
  );
}

function describeDevice(ua: string | null): { label: string; icon: NombreIcono } {
  if (!ua) return { label: 'Equipo desconocido', icon: 'mdi:devices' };
  const browser = /Edg\//.test(ua) ? 'Edge' : /OPR\//.test(ua) ? 'Opera' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Navegador';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS X/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : null;
  const mobile = /Android|iPhone|iPad|Mobile/.test(ua);
  return { label: os ? `${browser} en ${os}` : browser, icon: mobile ? 'mdi:cellphone' : 'mdi:monitor' };
}

function SessionsCard() {
  const q = useMySessions();
  const close = useCloseSession();
  const others = useCloseOtherSessions();
  const rows = q.data ?? [];
  const onError = (e: unknown) => avisar.error('No pudimos cerrar la sesión', { description: errorMessage(e) });
  const row = (x: UserSessionRow) => {
    const d = describeDevice(x.userAgent);
    return (
      <li key={x.id}>
        <Item variant="outline" size="sm">
          <ItemMedia><Icono nombre={d.icon} className="size-5 text-muted-foreground" aria-hidden /></ItemMedia>
          <ItemContent className="gap-0.5">
            <ItemTitle className="font-normal">{d.label}{x.current && <Badge variant="primary-light" size="sm">Esta sesión</Badge>}</ItemTitle>
            <ItemDescription className="text-xs tnum">{x.ip ?? 'IP desconocida'} · ingreso {fmtDate(x.createdAt)} · última actividad {fmtDate(x.lastActivityAt)}</ItemDescription>
          </ItemContent>
          {!x.current && <ItemActions><Button variant="ghost" size="sm" disabled={close.isPending} onClick={() => close.mutate(x.id, { onSuccess: () => avisar.success('Sesión cerrada'), onError })}><Icono nombre="mdi:logout" /> Cerrar</Button></ItemActions>}
        </Item>
      </li>
    );
  };
  return (
    <Card title="Sesiones abiertas" actions={rows.length > 1 ? <Button variant="outline" size="sm" disabled={others.isPending} onClick={() => others.mutate(undefined, { onSuccess: () => avisar.success('Cerramos tus otras sesiones'), onError })}>{others.isPending ? <Spinner /> : <Icono nombre="mdi:logout-variant" />} Cerrar las demás</Button> : undefined}>
      {q.isLoading ? <LoadingState rows={2} /> : q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : !rows.length ? <EmptyState title="Sin sesiones abiertas" icon="mdi:devices" /> : (
        <ul className="list-none m-0 p-0 flex flex-col gap-2">{rows.map(row)}</ul>
      )}
      <p className="text-xs text-muted-foreground mt-3 mb-0">Si ves un equipo que no reconoces, ciérralo y cambia tu contraseña en la suite.</p>
    </Card>
  );
}
